import { useEffect, useState, useMemo } from 'react'
import type {
  OpsRecord,
  OpsRequest,
  OpsTemplate,
  OutsideApprovalSubjectType,
  OutsideApprovalSettings,
} from '../../../types/ops'
import {
  buildApprovalKindKey,
  checkSubjectEligibility,
} from '../../../types/ops'
import { subscribeApprovalSettings } from '../../../services/opsApprovalsService'

export interface UseCanSendOutsideApprovalOptions {
  workspaceId: string
  subjectType: OutsideApprovalSubjectType
  subject: OpsRequest | OpsRecord | null | undefined
  template?: OpsTemplate | null
  userRole?: string | null
  isAdmin?: boolean
  currentUid?: string | null
  settings?: OutsideApprovalSettings | null
}

export interface UseCanSendOutsideApprovalResult {
  canSend: boolean
  reason?: string
  kindKey: string
}

export function useCanSendOutsideApproval({
  workspaceId,
  subjectType,
  subject,
  template,
  userRole,
  isAdmin,
  currentUid,
  settings: initialSettings,
}: UseCanSendOutsideApprovalOptions): UseCanSendOutsideApprovalResult {
  const [settings, setSettings] = useState<OutsideApprovalSettings | null>(initialSettings ?? null)

  useEffect(() => {
    if (initialSettings !== undefined || !workspaceId) return
    const unsub = subscribeApprovalSettings(workspaceId, (s) => {
      setSettings(s)
    })
    return () => unsub()
  }, [workspaceId, initialSettings])

  return useMemo(() => {
    if (!subject || !workspaceId || !currentUid) {
      return { canSend: false, reason: 'Missing subject or user', kindKey: '' }
    }

    const role = (userRole || '').toUpperCase()
    if (role === 'VIEWER' || role === 'HELPER') {
      return { canSend: false, reason: 'Role not authorized to send outside approvals', kindKey: '' }
    }

    // Check subject eligibility
    const rawDoc: Record<string, unknown> = {
      ...(subject as unknown as Record<string, unknown>),
      id: (subject as { id?: string }).id,
      ...(template ? { template } : {}),
    }

    const eligibility = checkSubjectEligibility(subjectType, rawDoc)
    if (!eligibility.eligible) {
      return { canSend: false, reason: eligibility.reason, kindKey: '' }
    }

    // Kind key
    const subKey =
      subjectType === 'REQUEST'
        ? String((subject as OpsRequest).kind || '')
        : String((subject as OpsRecord).templateId || '')
    const kindKey = buildApprovalKindKey(subjectType, subKey)

    // Admin / Owner bypasses kind restriction and readAccess check
    if (isAdmin || role === 'OWNER' || role === 'ADMIN') {
      return { canSend: true, kindKey }
    }

    // For Editor:
    // Check if kind is Admin-only
    const effectiveSettings = initialSettings !== undefined ? initialSettings : settings
    if (effectiveSettings?.adminOnlyKinds?.includes(kindKey)) {
      return { canSend: false, reason: 'This kind is configured as Admin-only', kindKey }
    }

    // Check read access
    if (subjectType === 'REQUEST') {
      const req = subject as OpsRequest
      const isRestricted = req.restricted === true || (req as unknown as { kindSnapshot?: { restricted?: boolean } }).kindSnapshot?.restricted === true
      if (isRestricted) {
        return { canSend: false, reason: 'Editor cannot send restricted request', kindKey }
      }
      const readAccess = Array.isArray(req.readAccess) ? req.readAccess : []
      if (readAccess.length > 0 && !readAccess.includes(currentUid)) {
        return { canSend: false, reason: 'Editor does not have read access to this place', kindKey }
      }
    } else {
      const rec = subject as OpsRecord
      const readAccess = Array.isArray(rec.readAccess) ? rec.readAccess : []
      if (readAccess.length > 0 && !readAccess.includes(currentUid)) {
        return { canSend: false, reason: 'Editor does not have read access to this place', kindKey }
      }
    }

    return { canSend: true, kindKey }
  }, [subject, subjectType, template, workspaceId, currentUid, userRole, isAdmin, settings, initialSettings])
}
